import React, {
    FC,
    useCallback,
    useEffect,
    useState,
} from 'react';

import {useCustomTable} from 'components/@common/widgets/custom-table/custom-table';
import {EmptyTablePlaceholder} from 'components/table';
import {ITableReportProps} from 'components/table-report/table-report-types';
import {useCalculateControlRatiosMutation, useFetchControlRatioStatisticTableMutation} from 'features/calculate-control-ratios/api/api';
import {showMessage} from 'shared/utils';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import {selectDocumentsIds, setSelectedDocumentsIds} from '../../model/slice';
import {IControlRatioStatisticDto} from '../../model/types';
import {getControlRatiosTableColumns} from '../columns/table-columns';

interface IControlRatiosTableProps {
    docId: ITableReportProps['docId'];
}

export const ControlRatiosTable: FC<IControlRatiosTableProps> = ({
    docId,
}: IControlRatiosTableProps) => {
    const dispatch = useAppDispatch();

    const [tableData, setTableData] = useState<IControlRatioStatisticDto[]>([]);
    const selectedDocuments = useAppSelector(selectDocumentsIds);

    const [fetchControlRatioStatisticTableRows, {
        isLoading: isTableLoading,
    }] = useFetchControlRatioStatisticTableMutation();

    const [calculateControlRatios] = useCalculateControlRatiosMutation();

    const fetchTableData = async () => {
        try {
            const result = await fetchControlRatioStatisticTableRows({
                docId,
                ids: selectedDocuments,
            });

            if ('data' in result && Array.isArray(result.data)) {
                setTableData(result.data);
                dispatch(setSelectedDocumentsIds(result.data.map(e => e.document.lookupCode).filter(e => e !== null)));
            }
        } catch (e) {
            showMessage({
                message: `Возникла ошибка при получении данных таблицы
                              документов для расчета контрольных соотношений`,
                isError: true,
            });
        }
    };

    useEffect(() => {
        fetchTableData();
    }, []);

    useEffect(() => {
        dispatch(setSelectedDocumentsIds(tableData.map(e => e.document.lookupCode).filter(e => e !== null)));
    }, [tableData]);

    const handleRowUpdate = useCallback((oldLookupCode: string, newLookupCode: string) => {
        setTableData(prevData => prevData.map(row => {
            if (row.document.lookupCode === oldLookupCode) {
                const selectedLookup = row.documentLookups.find(l => l.lookupCode === newLookupCode);

                if (!selectedLookup) return row;

                return {
                    ...row,
                    document: {
                        lookupCode: selectedLookup.lookupCode,
                        meaning: selectedLookup.meaning,
                    },
                };
            }

            return row;
        }));
    }, []);

    const handleCalculateCR = async (childDocId: ITableReportProps['docId']) => {
        try {
            const response = await calculateControlRatios({childDocId});

            if ('data' in response) showMessage({message: response.data});
        } catch (e) {
            showMessage({
                message: 'Возникла ошибка при расчете контрольных соотношений',
                isError: true,
            });
        }

        fetchTableData();
    };

    const {
        customTableJSX,
    } = useCustomTable({
        rowKey: 'id',
        dataSource: tableData,
        bordered: true,
        pagination: false,
        columns: getControlRatiosTableColumns({
            docId,
            handleRowUpdate,
            handleCalculateCR,
        }),
        locale: {
            emptyText: (
                <EmptyTablePlaceholder
                    isTableLoading={isTableLoading}
                    dataLength={tableData.length}
                />),
        },
    });

    return (
        <>
            {customTableJSX}
        </>
    );
};
